import {
  addDays,
  addMonths,
  addQuarters,
  addSeconds,
  addWeeks,
  addYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  fromUnixTime,
  isBefore,
  isValid,
  Locale,
  Month,
  parse,
  parseISO,
  startOfDay,
  subDays,
  subMonths,
  subQuarters,
  subSeconds,
  subWeeks,
  subYears,
} from 'date-fns';
import { format as formatTz, fromZonedTime, toZonedTime } from 'date-fns-tz';
import i18n from 'i18next';
import { enUS, es, fr, pl, ro, uk } from 'date-fns/locale';
import {
  dateFormat,
  timePickerDateFormat,
  timePickerDateFullFormat,
} from '@lib/enums/dateTime';
import daysEN from '@lib/locales/en/days.json';
import daysES from '@lib/locales/es/days.json';
import daysFR from '@lib/locales/fr/days.json';
import daysPL from '@lib/locales/pl/days.json';
import daysRO from '@lib/locales/ro/days.json';
import daysUK from '@lib/locales/uk/days.json';
// date-fns v4 does not expose `date-fns/locale/types` via its package exports
// map (only per-locale entry points are exported), so the LocaleWidth union is
// inlined here instead of imported from that internal path.
type LocaleWidth = 'narrow' | 'short' | 'abbreviated' | 'wide' | 'any';

// Take short month names (`MMM`) from the `month-N` translations, like dayjs
// does in `@lib/utils/dayjsLocale`, so date-fns labels match the date pickers
// and timeline headers (`Вер`, not date-fns' own `верес.`)
const withShortMonths = (
  locale: Locale,
  days: Record<string, string>,
): Locale => ({
  ...locale,
  localize: {
    ...locale.localize,
    month: (n: Month, options: { width?: LocaleWidth } = {}) =>
      options.width === 'abbreviated'
        ? days[`month-${n + 1}`]
        : locale.localize.month(n, options),
  },
});

export type DateArg = string | null | undefined | Date;

export const CET = 'Europe/Paris';

export function isTimestampExpired(timestamp: number | string): boolean {
  const now = new Date();
  const timestampDate = fromUnixTime(Number(timestamp));
  return isBefore(timestampDate, now);
}

export function getLastTimeFromArray(dates?: string[]) {
  if (!dates) {
    return null;
  }

  const validDates = dates
    .map((date) => parseISO(date))
    .filter((date) => isValid(date));
  if (validDates.length === 0) {
    return null;
  }

  return validDates.reduce((acc, currentDate) =>
    isBefore(acc, currentDate) ? currentDate : acc,
  );
}

export function toIsoStringWithMicroseconds(date: Date): string {
  if (!date) return date;
  // Convert date to CET before formatting
  const cetDate = toZonedTime(date, CET);
  let isoString = cetDate.toISOString();
  const parts = isoString.split('.');
  const milliseconds = parts[1].substring(0, 3);
  let secondPartOfMilliseconds = '000';
  if (milliseconds === '999') {
    secondPartOfMilliseconds = '999';
  }
  // Construct ISO string with CET time and artificial microseconds
  isoString = `${parts[0]}.${milliseconds}${secondPartOfMilliseconds}Z`;
  return isoString;
}

export function formatTimeToISODateString(timeString: string): string {
  if (!timeString) return timeString;
  const [hours = 0, minutes = 0, seconds = 0] = timeString
    .split(':')
    .map(Number);
  // Create a date with the current date but specified time
  const now = new Date();
  now.setHours(hours, minutes, seconds, 0);
  // Convert this time to CET before formatting
  const cetDate = fromZonedTime(now, CET); // Note: This converts the local time to equivalent UTC time for CET zone
  return toIsoStringWithMicroseconds(cetDate);
}

export function transformDate(
  date: Date,
  setEndDate = false,
  setStartDate = false,
): string {
  let newDate = date;
  if (setEndDate) newDate = endOfDay(date);
  if (setStartDate) newDate = startOfDay(date);
  const formatPattern = setEndDate
    ? "yyyy-MM-dd'T'HH:mm:ss.SSS'999'XXX"
    : "yyyy-MM-dd'T'HH:mm:ss.SSS'000'XXX";
  // Format date in CET
  return formatTz(newDate, formatPattern, { timeZone: CET });
}

export function revertTransformDate(value: string) {
  return toZonedTime(parseISO(value), CET);
}

// A bare `YYYY-MM-DD` string fed directly into a DatePicker `value` prop is
// parsed as LOCAL midnight by `revertTransformDate` → its CET wall-clock
// conversion drops a day for users east of CET. The fix is NOT to route
// through `transformDate`: `date-fns-tz`'s `format` only uses its `timeZone`
// option to compute the offset suffix (XXX) — the actual date/time digits
// are produced by plain `date-fns` `format`, which reads the Date via the
// CALLER'S OWN system-timezone getters. So `transformDate(fromZonedTime(...))`
// still leaks the caller's local timezone into the digits and only happens to
// land on the right calendar day for some timezones. Instead, keep the `ymd`
// digits completely untouched (there's no time-of-day to convert) and only
// use timezone math to look up CET's offset for that date (which correctly
// handles the CET/CEST DST boundary), producing a value that round-trips
// through `revertTransformDate` correctly regardless of the caller's own
// timezone.
export function dateOnlyToDatePickerValue(ymd?: string | null): string | null {
  if (!ymd) return null;
  const cetInstant = fromZonedTime(parseISO(ymd), CET);
  const offset = formatTz(cetInstant, 'XXX', { timeZone: CET });
  return `${ymd}T00:00:00.000000${offset}`;
}

// CET/CEST offset in force at a CET wall-clock `ymd` + `time`.
function getCetOffset(ymd: string, time: string) {
  return formatTz(fromZonedTime(`${ymd}T${time}`, CET), 'XXX', {
    timeZone: CET,
  });
}

// API datetimes spanning CET calendar days `startYmd`..`endYmd` inclusive —
// first CET midnight to last CET end-of-day — for `gteq`/`lteq` filters that
// must match the backend's Europe/Paris day buckets. Like
// `dateOnlyToDatePickerValue`, the digits never pass through the caller's
// own timezone.
export function getCetDayRangeBounds(startYmd: string, endYmd: string) {
  return {
    start: `${startYmd}T00:00:00.000000${getCetOffset(startYmd, '00:00:00')}`,
    end: `${endYmd}T23:59:59.999999${getCetOffset(endYmd, '23:59:59')}`,
  };
}

const dateFnsLocales = {
  en: withShortMonths(enUS, daysEN),
  es: withShortMonths(es, daysES),
  fr: withShortMonths(fr, daysFR),
  pl: withShortMonths(pl, daysPL),
  ro: withShortMonths(ro, daysRO),
  uk: withShortMonths(uk, daysUK),
};

function getDateFnsLocale(language?: string) {
  const code = (language || i18n.language || 'en').split('-')[0];
  return dateFnsLocales[code as keyof typeof dateFnsLocales] ?? enUS;
}

export function getFormattedDate(
  value?: DateArg,
  customFormat?: string,
  locale?: string,
) {
  if (!value) return null;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return formatTz(date, customFormat || dateFormat, {
    timeZone: CET,
    // @ts-ignore
    locale: getDateFnsLocale(locale),
  });
}

export function subYearsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(subYears(date, amount));
}

export function subQuartersFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(subQuarters(date, amount));
}

export function subMonthsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(subMonths(date, amount));
}

export function subWeeksFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfWeek(subWeeks(date, amount), { weekStartsOn: 1 });
}

export function subDaysFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return subDays(date, amount);
}

export function subSecondsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return subSeconds(date, amount);
}

export function addYearsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(addYears(date, amount));
}

export function addQuartersFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(addQuarters(date, amount));
}

export function addMonthsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(addMonths(date, amount));
}

export function addWeeksFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfWeek(addWeeks(date, amount), { weekStartsOn: 1 });
}

export function addDaysFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return addDays(date, amount);
}

export function addSecondsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return addSeconds(date, amount);
}

export function startOfDayFn(value: DateArg) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return startOfDay(date);
}

export function endOfDayFn(value: DateArg) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfDay(date);
}

/**
 * Converts a time string to HH:mm format
 * @param time
 * @returns A string in HH:mm format
 */
export function convertTimeToHHmm(time?: unknown): string | undefined {
  try {
    if (typeof time === 'string' && !!time) {
      const parsedTime = parse(time, timePickerDateFullFormat, new Date());
      return format(parsedTime, timePickerDateFormat);
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
}

/**
 * Converts a decimal time value to `hh:mm` format.
 * @param decimalValue - The decimal time value to convert (e.g., 10.10).
 * @returns The formatted time string in `hh:mm` format.
 */
export function convertDecimalHoursToTime(decimalValue: number): string {
  const isNegative = decimalValue < 0;
  const absoluteValue = Math.abs(decimalValue);

  const hours = Math.floor(absoluteValue);
  const minutes = Math.round((absoluteValue - hours) * 60);

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');

  return isNegative
    ? `-${formattedHours}:${formattedMinutes}`
    : `${formattedHours}:${formattedMinutes}`;
}

/**
 * Converts a decimal time value in minutes to `mm:ss` format.
 * @param decimalValue - The decimal time value to convert (e.g., 10.10).
 * @returns The formatted time string in `mm:ss` format.
 */
export function convertDecimalMinutesToTime(decimalValue: number): string {
  const isNegative = decimalValue < 0;
  const absoluteValue = Math.abs(decimalValue);

  const minutes = Math.floor(absoluteValue);
  const seconds = Math.round((absoluteValue - minutes) * 60);

  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return isNegative
    ? `-${formattedMinutes}:${formattedSeconds}`
    : `${formattedMinutes}:${formattedSeconds}`;
}

/**
 * Converts a decimal time value in seconds to `ss:milliseconds` format.
 * @param decimalValue - The decimal time value to convert (e.g., 10.10).
 * @returns The formatted time string in `ss:milliseconds` format.
 */
export function convertDecimalSecondsToTime(decimalValue: number): string {
  const isNegative = decimalValue < 0;
  const absoluteValue = Math.abs(decimalValue);

  const seconds = Math.floor(absoluteValue);
  const milliseconds = Math.round((absoluteValue - seconds) * 1000);

  const formattedSeconds = String(seconds).padStart(2, '0');
  const formattedMilliseconds = String(milliseconds).padStart(3, '0'); // Ensure 3 digits for milliseconds

  return isNegative
    ? `-${formattedSeconds}:${formattedMilliseconds}`
    : `${formattedSeconds}:${formattedMilliseconds}`;
}

/**
 * Converts a seconds value to a formatted time string.
 * @param seconds - The seconds time value to convert.
 * @returns The formatted time string in `±Xd Xh Xm` format.
 */
export function convertSecondsToTime(
  seconds: number | null | undefined,
): string | undefined {
  if (seconds === null || seconds === undefined) return undefined;

  const isNegative = seconds < 0;
  const absoluteSeconds = Math.abs(seconds);

  const totalMinutes = Math.floor(absoluteSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const days = Math.floor(hours / 24);
  // const remainingSeconds = absoluteSeconds % 60;

  const formattedDays = `${days}d`;
  const formattedHours = `${hours % 24}h`;
  const formattedMinutes = `${minutes}m`;
  // const formattedSeconds = `${remainingSeconds}s`;

  return `${isNegative ? '-' : ''}${formattedDays} ${formattedHours} ${formattedMinutes}`;
}

/**
 * Formats a playback position in seconds the way media players show it:
 * `m:ss`, or `h:mm:ss` past an hour (e.g. 180 -> "3:00", 3723 -> "1:02:03").
 * @param totalSeconds - Seconds from the start; fractions are floored.
 * @returns The formatted position, "0:00" for a negative or non-finite value.
 */
export function formatPlaybackTime(totalSeconds: number): string {
  const safeSeconds = Number.isFinite(totalSeconds)
    ? Math.max(0, Math.floor(totalSeconds))
    : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = String(safeSeconds % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
}

/**
 * Formats a playback range as `start–end` (e.g. "3:00–4:30"), or just the
 * start when the end is unknown (null).
 */
export function formatPlaybackRange(
  startSeconds: number,
  endSeconds: number | null,
): string {
  const start = formatPlaybackTime(startSeconds);
  return endSeconds === null
    ? start
    : `${start}–${formatPlaybackTime(endSeconds)}`;
}
